import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Avatar, TAG_COLOR, tagLabel } from '../components/Feed';
import { Collapse, Pause, Play, Redo, SkipNext, Wave } from '../components/Icons';
import { StatusBanner } from '../components/Status';
import { LangSwitch } from '../components/Voice';
import { fmt, useT } from '../lib/i18n';
import { session, useSession } from '../lib/session';
import { updateSettings, useSettings } from '../lib/settings';
import { unlockAudio } from '../lib/sfx';

const PAGE_KIND = 'reader';

export function Reader() {
  const t = useT();
  const s = useSession();
  const st = useSettings();
  const nav = useNavigate();
  const { username = '' } = useParams();

  useEffect(() => {
    if (!username) {
      nav('/');
      return;
    }
    unlockAudio();
    session.start(username, { page: PAGE_KIND });
  }, [username, nav]);

  const sp = s.speech;
  const current = sp.current;
  const paused = sp.paused;
  const idle = !current || !st.voiceOn;
  const label = !st.voiceOn ? t.voiceOff : paused ? t.paused : current ? t.readingAloud : t.waiting;
  const live = s.status === 'live';

  return (
    <div className="screen screen--wide">
      <div className="reader">
        <main className="reader__main">
          <header className="reader__head">
            <Link to={`/live/${username}`} className="iconbtn iconbtn--filled" aria-label={t.compact}>
              <Collapse />
            </Link>
            <span style={{ fontSize: 18, fontWeight: 700 }}>@{username}</span>
            {live && <span className="tag tag--live" style={{ height: 22, padding: '0 8px', fontSize: 12 }}>{t.live}</span>}
            <span style={{ marginLeft: 'auto', fontSize: 16, color: 'var(--muted)' }}>
              <strong style={{ color: 'var(--text)' }}>{s.stats.viewers.toLocaleString()}</strong> {t.watching}
            </span>
          </header>

          <StatusBanner s={s} t={t} />

          <section aria-live="polite" className="reader__now">
            <div className={`reader__status${idle || paused ? ' reader__status--idle' : ''}`}>
              <Wave big paused={idle || paused} />
              {label}
            </div>
            {current && (
              <div className="reader__user">
                <Avatar user={current.user} size="xl" />
                <span>{current.user.nickname || current.user.uniqueId}</span>
                {TAG_COLOR[current.kind] && (
                  <span className="tag" style={{ background: TAG_COLOR[current.kind]!, height: 24, fontSize: 12 }}>
                    {tagLabel(current.kind, t)}
                  </span>
                )}
              </div>
            )}
            <p className={`reader__text${current ? '' : ' reader__text--empty'}`} style={{ fontSize: current ? st.readerSize : Math.round(st.readerSize * 0.5) }}>
              {current ? current.text : t.nothingYet}
            </p>
          </section>

          <div className="reader__actions">
            <button type="button" className="btn" onClick={() => (paused ? session.tts.resume() : session.tts.pause())}>
              {paused ? <Play size={20} /> : <Pause size={20} />}
              {paused ? t.resume : t.pause}
            </button>
            <button type="button" className="btn" onClick={() => session.tts.skip()}>
              <SkipNext size={20} />
              {t.skip}
            </button>
            <button type="button" className="btn" onClick={() => session.tts.readAgain()} disabled={!current && sp.history.length === 0}>
              <Redo />
              {t.readAgain}
            </button>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn--sq" aria-label={t.smaller} onClick={() => updateSettings({ readerSize: Math.max(40, st.readerSize - 8) })}>
                A−
              </button>
              <button type="button" className="btn btn--sq" aria-label={t.bigger} onClick={() => updateSettings({ readerSize: Math.min(112, st.readerSize + 8) })}>
                A+
              </button>
            </div>
          </div>
        </main>

        <aside className="reader__aside">
          <h2>
            <span>{t.upNext}</span>
            <span>{sp.queue.length}</span>
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sp.queue.slice(0, 4).map((q) => (
              <div key={q.id} className="upnext">
                <span className="upnext__name">
                  {q.user.nickname || q.user.uniqueId}
                  {TAG_COLOR[q.kind] && (
                    <span className="tag" style={{ background: TAG_COLOR[q.kind]! }}>
                      {tagLabel(q.kind, t)}
                    </span>
                  )}
                </span>
                <span className="upnext__text">{q.text}</span>
              </div>
            ))}
          </div>
          <h2 style={{ marginTop: 16 }}>
            <span>{t.justRead}</span>
          </h2>
          <div className="justread">
            {sp.history.slice(0, 5).map((h, i) => (
              <span key={h.id + i}>
                <strong>{h.user.nickname || h.user.uniqueId}</strong> {h.text}
              </span>
            ))}
          </div>
          <div className="voicepill">
            <span style={{ color: 'var(--muted)' }}>
              {t.voice} · {st.rate.toFixed(1)}×
            </span>
            <LangSwitch current={current} size="lg" />
          </div>
          <span className="voices-note">{fmt(t.inQueue, { n: sp.queue.length })}</span>
        </aside>
      </div>
    </div>
  );
}
